import {simplifySqrtLatex} from "../../../../utils/sqrtUtils";
﻿import { InlineMath, BlockMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

const options = [
  { value: "przecina", label: "Przecina okrąg" },
  { value: "styczna", label: "Jest styczna do okręgu" },
  { value: "oddalona", label: "Jest rozłączna z okręgiem" },
];

const positions = options.map(({ value }) => value);
const integerLengthNormals = [
  { A: 0, B: 1, length: 1 },
  { A: 3, B: 4, length: 5 },
  { A: 4, B: 3, length: 5 },
  { A: -3, B: 4, length: 5 },
  { A: -4, B: 3, length: 5 },
];

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

// (x - a)^2, ale dla a<0 daje (x + |a|)^2, dla a=0 daje x^2
function formatShiftedSquare(varName, a) {
  if (a === 0) return `${varName}^2`;
  const sign = a < 0 ? "+" : "-";
  const val = Math.abs(a);
  return `(${varName} ${sign} ${val})^2`;
}

// współczynnik przy zmiennej: 2x, -x, x (bez 1)
function formatCoeffVar(coeff, varName) {
  if (coeff === 0) return "";
  if (coeff === 1) return `${varName}`;
  if (coeff === -1) return `-${varName}`;
  return `${coeff}${varName}`;
}

function formatParenIfNeg(n) {
  return n < 0 ? `(${n})` : `${n}`;
}

// najprostsze skracanie ułamka
function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function simplifyFraction(numer, denom) {
  const g = gcd(numer, denom);
  return { numer: numer / g, denom: denom / g };
}

export function generateTask(settings = {}) {
  const a = getRandomInt(-5, 5);
  const b = getRandomInt(-5, 5);
  const r = getRandomInt(2, 6);
  const requestedPosition = positions.includes(settings?.position)
    ? settings.position
    : positions[getRandomInt(0, positions.length - 1)];
  const normal = integerLengthNormals[getRandomInt(0, integerLengthNormals.length - 1)];
  const { A, B, length } = normal;
  const distance =
    requestedPosition === "styczna"
      ? r
      : requestedPosition === "przecina"
        ? getRandomInt(0, r - 1)
        : getRandomInt(r + 1, r + 3);
  const side = Math.random() < 0.5 ? -1 : 1;
  const C = side * distance * length - A * a - B * b;

  return {
    type: "LineCirclePositionTask",
    data: {
      circle: { a, b, r },
      line: { A, B, C },
      position: requestedPosition,
    },
  };
}

export function renderTask(task) {
  const data = task.data ?? task;
  const { circle, line } = data;

  const { a, b, r } = circle;
  const { A, B, C } = line;

  // ✅ (y + 1)^2 zamiast (y - -1)^2
  const circleEq = `${formatShiftedSquare("x", a)} + ${formatShiftedSquare(
    "y",
    b
  )} = ${r ** 2}`;

  // ✅ bez "1y" i ładne znaki
  const Ax = formatCoeffVar(A, "x");
  const By = formatCoeffVar(B, "y");

  let lineEq = "";
  if (Ax) lineEq += Ax;

  if (By) {
    if (!lineEq) lineEq += By;
    else lineEq += (B >= 0 ? " + " : " - ") + formatCoeffVar(Math.abs(B), "y");
  }

  if (C !== 0) {
    lineEq += (C >= 0 ? " + " : " - ") + Math.abs(C);
  }

  if (!lineEq) lineEq = "0";
  lineEq += " = 0";

  return (
    <div className="space-y-2">
      <p>Określ wzajemne położenie okręgu i prostej:</p>
      <BlockMath math={circleEq} />
      <BlockMath math={lineEq} />
      <p>Wybierz odpowiedź:</p>
    </div>
  );
}

export function renderInput(task, userInput, setUserInput, disabled) {
  const handleChange = (eOrValue) => {
    const value =
      typeof eOrValue === 'string'
        ? eOrValue
        : eOrValue?.target?.value ?? eOrValue?.value ?? '';

    setUserInput(value);
  };

  const safeValue =
    typeof userInput === 'string'
      ? userInput
      : userInput?.target?.value ?? userInput?.value ?? '';

  return (
    <SelectInput
      value={safeValue}
      onChange={handleChange}
      options={options}
      disabled={disabled}
    />
  );
}

export function checkAnswer(task, userInput) {
  const data = task.data ?? task;
  return userInput === data.position;
}

export function renderAnswer(task) {
  const data = task.data ?? task;
  return options.find((o) => o.value === data.position)?.label ?? "";
}

export function formatLineCircleDistance(numerator, denominatorSquared) {
  if (numerator === 0) return {suffix:" = 0", value:"= 0"};
  const root=Math.sqrt(denominatorSquared);
  if (Number.isInteger(root)) {
    const {numer,denom}=simplifyFraction(numerator,root);
    let rest=denom;
    while(rest%2===0)rest/=2;
    while(rest%5===0)rest/=5;
    const exact=denom===1?String(numer):rest===1?String(numer/denom).replace(".","{,}"):String.raw`\frac{${numer}}{${denom}}`;
    return {suffix:String.raw` = \frac{${numerator}}{${root}} = ${exact}`,value:`= ${exact}`};
  }
  let outside=1;
  for(let i=2;i*i<=denominatorSquared;i++) if(denominatorSquared%(i*i)===0)outside=i;
  const inside=denominatorSquared/(outside*outside);
  const simplified=simplifySqrtLatex(denominatorSquared);
  const {numer,denom}=simplifyFraction(numerator,outside*inside);
  const top=String.raw`${numer===1?"":numer}\sqrt{${inside}}`;
  const exact=denom===1?top:String.raw`\frac{${top}}{${denom}}`;
  const reduced=outside===1?"":String.raw` = \frac{${numerator}}{${simplified}}`;
  const rounded=(numerator/root).toFixed(2).replace(".","{,}");
  return {suffix:String.raw`${reduced} = ${exact} \approx ${rounded}`,value:String.raw`= ${exact} \approx ${rounded}`};
}

export function renderExplanation(task) {
  const data = task.data ?? task;
  const { circle, line, position } = data;

  const { a, b, r } = circle;
  const { A, B, C } = line;

  const exprValue = A * a + B * b + C;
  const numeratorAbs = Math.abs(exprValue);
  const denomSq = A * A + B * B;

  const d = numeratorAbs / Math.sqrt(denomSq);

  // ✅ nawiasy przy ujemnych w podstawieniu: 1·(-1), (-2)·4 itd.
  const mulA = `${formatParenIfNeg(A)}\\cdot ${formatParenIfNeg(a)}`;
  const mulB = `${formatParenIfNeg(B)}\\cdot ${formatParenIfNeg(b)}`;
  const addC = formatParenIfNeg(C);

  const denomSqLatex = `${formatParenIfNeg(A)}^2 + ${formatParenIfNeg(B)}^2`;
  const denomLatex = `\\sqrt{${denomSqLatex}}`;

  // ✅ usunięcie niewymierności: n/√k = n√k/k (z ewentualnym skróceniem)
  const distanceDisplay = formatLineCircleDistance(numeratorAbs, denomSq);

  const cmpLatex =
    Math.abs(d - r) < 1e-6 ? "d = r" : d < r ? "d < r" : "d > r";

  // --- RYSUNEK: dynamiczny zakres ---
  const pad = 2;
  const xmin = a - r - pad;
  const xmax = a + r + pad;
  const ymin = b - r - pad;
  const ymax = b + r + pad;

  // Równanie prostej w formie, którą GraphViewer rysuje (w innych taskach działa)
  // Uwaga: C w nawiasie, żeby ujemne nie robiło " + -3"
  const lineExpr = `y = ${(-A / B).toFixed(6)}*x + (${(-C / B).toFixed(6)})`;
  // Alternatywa (jeśli wolisz "Ax + By + C = 0", ale GraphViewer zwykle lepiej łyka y=mx+c):
  // const lineExpr = `${A}*x + ${B}*y + (${C}) = 0`;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Odczytujemy dane:
        <BlockMath math={`S = (${a}, ${b}) \\quad r = ${r}`} />
        <BlockMath
          math={`Ax + By + C = 0 \\quad A = ${A},\\ B = ${B},\\ C = ${C}`}
        />
      </div>

      <div>
        <strong>Krok 2:</strong> Liczymy odległość środka od prostej:
        <BlockMath
          math={
            `d = \\frac{|A\\cdot a + B\\cdot b + C|}{\\sqrt{A^2 + B^2}}` +
            ` = \\frac{|${mulA} + ${mulB} + ${addC}|}{${denomLatex}}` +
            ` = \\frac{${numeratorAbs}}{\\sqrt{${denomSq}}}` +
            distanceDisplay.suffix
          }
        />
      </div>

      <div>
        <strong>Krok 3:</strong> Porównujemy wartości:
        <BlockMath math={`r = ${r} \\quad d ${distanceDisplay.value}`} />
        <p>
          Ponieważ <InlineMath math={cmpLatex} />, prosta{" "}
          <strong>{options.find((o) => o.value === position).label}</strong>.
        </p>
      </div>

      {/* RYSUNEK */}
      <div style={{ marginTop: "2rem" }}>
        <GraphViewer
          circles={[{ center: { x: a, y: b }, radius: r, label: "O" }]}
          points={{
            S: { x: a, y: b, label: "S" },
          }}
          // Jeśli Twój GraphViewer wspiera obiekty, kolor zadziała:
          equations={[{ expr: lineExpr, color: "#00c466", label: "l" }]}
          // Jeśli nie wspiera obiektów, użyj tego:
          // equations={[lineExpr]}
          showGrid
          width={500}
          height={500}
          xRange={[xmin, xmax]}
          yRange={[ymin, ymax]}
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput) {
  const label = options.find((o) => o.value === userInput)?.label;
  return label || <i>(brak odpowiedzi)</i>;
}
